import { DataTableColumn, DataTableRow } from '../types';

/**
 * Creates a map of columns by their IDs for O(1) lookup
 * @param columns - Array of data table columns
 * @returns Map object with column IDs as keys and full column objects as values
 */
export function createColumnsMap<T extends DataTableRow>(
  columns: DataTableColumn<T>[]
): Record<string, DataTableColumn<T>> {
  const map: Record<string, DataTableColumn<T>> = {};

  columns.forEach((column) => {
    map[column.id] = column;
  });

  return map;
}

/**
 * Gets the sort field ID from a column
 * @param column - The column to get sort field from
 * @returns The sortFieldId if defined, otherwise the column id
 */
export function getColumnSortField<T extends DataTableRow>(column: DataTableColumn<T>): string {
  return column.sortFieldId || column.id;
}

/**
 * Gets sort field from columns map by column ID
 * @param columnsMap - Map of columns by ID
 * @param columnId - The ID of the column to get sort field for
 * @returns The sort field ID or the column ID if not found
 */
export function getSortFieldFromMap<T extends DataTableRow>(
  columnsMap: Record<string, DataTableColumn<T>>,
  columnId: string
): string {
  const column = columnsMap[columnId];
  return column ? getColumnSortField(column) : columnId;
}

/**
 * Finds column ID by sort field
 * @param columns - Array of columns
 * @param sortField - The sort field to find column for
 * @returns The column ID or the sortField if no match found
 */
export function getColumnIdBySortField<T extends DataTableRow>(
  columns: DataTableColumn<T>[],
  sortField: string
): string {
  const column = columns.find((col) => (col.sortFieldId || col.id) === sortField);
  return column?.id || sortField;
}
